import { z } from 'zod';
import { maxCodePoints } from '@/lib/forms/primitives';
import { msg } from '@/lib/forms/messages';

const WHITE_SPACE = /[\p{White_Space}\ufeff]/u;

// Blank if either runtime trims it away: Python strip() also removes U+0085 and U+001C-U+001F,
// JS trim() also removes U+FEFF.
export const isBlank = (value: string) => {
  for (const char of value) {
    if (!WHITE_SPACE.test(char) && !(char >= '\x1c' && char <= '\x1f')) return false;
  }
  return true;
};

const NUL = String.fromCharCode(0);
const noNul = z.refine<string>((value) => !value.includes(NUL), msg('drbfm.firstRunInput.nulNotAllowed'));

// Caps are the smallest backend limit on either path the first run can take:
// - itemName: StartMainline.item_name max_length=200 (application/drbfm/mainline_contract.py:166);
//   drbfm_sessions.item_name allows 500.
// - title: drbfm_sessions.title VARCHAR(500) (017_drbfm_tables.sql).
// - supplement: 8000 on both #1609's supplement and the mainline Inputs.constraints, counted
//   before the backend strips it, so the raw value is measured.
// - specification text: the mainline Inputs old_text / new_text / report_text max_length=100000.
// Postgres text and #1609 reject NUL, so no field may contain one.
export const firstRunBasicInfoSchema = z.object({
  itemName: z
    .string()
    .trim()
    .refine((value) => !isBlank(value), msg('drbfm.firstRunInput.itemNameRequired'))
    .check(noNul, maxCodePoints(200, 'validation.maxLength')),
  title: z.string().trim().check(noNul, maxCodePoints(500, 'validation.maxLength')),
  supplement: z.string().check(noNul, maxCodePoints(8000, 'validation.maxLength')),
});

export const firstRunSpecificationTextSchema = z
  .string()
  .check(noNul, maxCodePoints(100000, 'validation.maxLength'));
